import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import moment from "moment";
import i18next from "i18next";

import SharedButton from "../../components/Shared/SharedButton";
import SharedTooltip from "../../components/Shared/SharedTooltip";
import InsuranceClaimTable from "./InsuranceClaimTable";
import { debounce } from "../../helpers/debounce";
import Filters from "./Filters";
import FilterDetails from "../Invoice/FilterDetails";
import ExportInvoices from "../Invoice/ExportInvoices";
import supergitHIS from "../../api/supergitHIS";
import validSupergitHIS from "../../api/validSupergitHIS";
import { FaFileExcel } from "react-icons/fa6";
import * as XLSX from "xlsx";
import { formatDate } from "../Transactions/TransactionsTable";
import SearchField from "../../components/Shared/SearchField";

export default function InsuranceClaimRevokeInvoices({
  ThirdParty = false,
  activeTab,
}) {
  const key = "insurance-claim-revoke-invoice";
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filteredInvoices, setFilteredInvoices] = useState(null);
  const [listData, setListData] = useState([]);
  const [isListLoading, setIsListLoading] = useState(false);
  const [filterLoading, SetFiltersLoading] = useState(false);
  const [filtersDetails, setFiltersDetails] = useState(null);
  const currentDate = moment().format("YYYY-MM-DD");
  const firstDayOfCurrentMonth = moment().startOf("month").format("YYYY-MM-DD");
  const lang = i18next.language;
  const [searchText, setSearchText] = useState("");

  const [filters, setFilters] = useState(
    localStorage.getItem(key)
      ? JSON.parse(localStorage.getItem(key))
      : {
          start_date: firstDayOfCurrentMonth,
          end_date: currentDate,
          branch_id: "",
          insurance_company_id: "",
          tpa: "",
          query: "",
          page: 1,
          per_page: 10,
        },
  );

  const fetchListData = async (payload = filters) => {
    setIsListLoading(true);
    try {
      const response = await validSupergitHIS.post(
        "revoked_insurance_claim_invoices/list",
        {
          ...payload,
          branch_id: String(payload?.branch_id),
          insurance_company_id: String(payload?.insurance_company_id),
        },
      );
      setListData(response.data || []);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setIsListLoading(false);
    }
  };

  useEffect(() => {
    fetchListData(filters);
  }, []);

  const handleSearch = (event) => {
    const value = event?.target?.value;
    setSearchText(value);
    debouncedSearch(value);
  };

  const debouncedSearch = useCallback(
    debounce(async (value) => {
      const trimmedValue = value?.trim();

      const payload = {
        ...filters,
        query: trimmedValue,
      };

      setFilters(payload);
      fetchListData(payload);
    }, 500),
    [filters],
  );

  const getNestedValue = (obj, path) => {
    path.split(".").reduce((acc, key) => acc?.[key], obj);
  };
  const handleExcelExport = () => {
    if (!listData?.data || listData.data.length === 0) return;

    const fields = [
      { key: "date", label: "Date", format: formatDate },
      { key: "invoice_no", label: "Invoice No" },
      { key: "insurance_company.name_en", label: "Insurance Company" },
      { key: "branch.name_en", label: "Branch" },
      { key: "total_gross", label: "Total Gross" },
      { key: "total_discount", label: "Total Discount" },
      { key: "total_deduction", label: "Total Deduction" },
      { key: "total_net", label: "Total Net" },
    ];

    const excelRows = listData.data.map((item) => {
      const row = {};
      fields.forEach(({ key, label, format }) => {
        const value = getNestedValue(item, key);
        row[label] = format ? format(value) : value;
      });
      return row;
    });
    const worksheet = XLSX.utils.json_to_sheet(excelRows);

    worksheet["!cols"] = [
      { wch: 25 },
      { wch: 20 },
      { wch: 50 },
      { wch: 20 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
    ];

    worksheet["!rows"] = listData.data.map(() => ({ hpt: 20 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Export");

    XLSX.writeFile(workbook, "invoice_export.xlsx");
  };

  return (
    <>
      <div className="">
        <div
          className={`flex ${
            !ThirdParty ? "justify-between" : "justify-end"
          } flex-col gap-2 sm:gap-0 sm:flex-row mb-3`}
        >
          <SharedTooltip
            content={t("Keep_track_of_your_invoices")}
            position="left"
            id="Items"
          />
          {!ThirdParty && (
            <span
              data-tooltip-id="Items"
              className="font-semibold text-black/70 text-xl mt-2"
            >
              {t("insurance_claim_invoice")}
              <span className="italic text-lg ml-1">
                <span className="text-xs font-bold italic">
                  ( {listData?.total_record || 0} {t("Entries")})
                </span>
              </span>
            </span>
          )}
          <div className="flex gap-2">
            <div className="flex gap-2">
              <SearchField
                placeholder={t("Search by invoice number")}
                value={searchText}
                onChange={handleSearch}
                handleClearClick={() => setSearchText("")}
                className="w-full sm:w-[250px]"
              />
            </div>

            <SharedButton
              title={t("Export to Excel")}
              icon="pi pi-file-excel"
              onClick={handleExcelExport}
            />
          </div>
        </div>

        <Filters
          storageKey={key}
          filters={filters}
          setFilters={setFilters}
          setSearchText={setSearchText}
          filterLoading={filterLoading}
          handleAddFilters={() => {}}
          handleResetFilters={() => {}}
          filteredInvoices={filteredInvoices}
          fetchListData={fetchListData}
          loading={isListLoading}
        />
        <InsuranceClaimTable
          companyInvoicesList={listData}
          activeTab={activeTab}
          ThirdParty={ThirdParty}
          loading={isListLoading}
          filters={filters}
          setFilters={setFilters}
          fetchListData={fetchListData}
          setFilteredInvoices={setFilteredInvoices}
          SetFiltersLoading={SetFiltersLoading}
          setFiltersDetails={setFiltersDetails}
          revoke
        />
        {/* <FilterDetails filtersDetails={filteredInvoices ? filtersDetails : companyInvoicesList} loading={isLoading} /> */}
      </div>
    </>
  );
}
